/**
 * CotacaoLadoALado — vista "lado a lado" da cotacao.
 *
 * Gira a grade da `CotacaoRespostas`: produtos nas LINHAS, fornecedores nas COLUNAS
 * (2 colunas por fornecedor: descricao do fornecedor + unitario). Serve pra COMPARAR
 * e DECIDIR — por isso os checkboxes I/D/F vivem aqui tambem, embaixo do unitario,
 * no lugar onde antes ficava o subtotal (unitario x qtd, hoje so no `title` da celula).
 * Sao os MESMOS estados da vista "Por produto": marcar de um lado reflete no outro.
 * Editar celula (preco, codigo, descricao) e remover variacao seguem so la.
 *
 * Ocupa a tela inteira (abaixo do menu do sistema) porque o ganho e vertical: a faixa
 * de cabecalho da vista normal (chips + busca + expandir/fechar) come ~200px e some aqui,
 * o que vale ~5 produtos a mais na tela.
 *
 * O ranking (amarelo = menor unitario, verde = compra forcada) usa EXATAMENTE a mesma
 * conta da `ProdutoFornecedorTable` — se divergir, as duas vistas mostrariam vencedores
 * diferentes pro mesmo dado.
 */
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { ArrowLeft, ChevronDown, ChevronsLeft, ChevronsRight, ChevronUp, LayoutGrid, List, Loader2, RefreshCw, Search, ShoppingCart, X } from 'lucide-react';
import { formatMoeda, formatMoedaSemSimbolo, formatQtd, formatDataSP, getNomeFornecedor } from '@fiodecobre/shared';
import type { ItemCotacao, FornecedorVinculado, RespostaForm } from './CotacaoRespostas';
import { alvoDaMarcaCompra, itemInativo } from './CotacaoRespostas';
import { MSG_PRODUTO_INATIVO, SeloInativo } from '../../lib/produto-inativo';
import { SeloFilial } from './CotacaoRespostas';
import { AvisoOcultosComprados } from './AvisoOcultosComprados';

// So pra COMPARAR datas (nunca exibir). Copia local da mesma conta da `CotacaoRespostas`
// (evita import circular entre as duas vistas) — mexeu numa, mexer na outra.
function paraDataMs(s: string | null | undefined): number {
  if (!s) return 0;
  const iso = s.includes('T') || s.endsWith('Z') ? s : s.replace(' ', 'T') + (/[+-]\d{2}$/.test(s) ? ':00' : '');
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

// Mesmas cores de cabecalho da grade normal (ProdutoFornecedorTable).
const TH_BG = '#22d3ee';
const TH_FG = '#0c4a6e';
const DIVISOR = '#0891b2';

// A faixa fixa da esquerda e o que sobra de largura pros fornecedores: cada fornecedor custa
// ~262px medidos, entao cada ~262px economizados aqui valem UMA coluna de fornecedor a mais na
// tela. Por isso o codigo interno mora embaixo da foto (a coluna Cod. propria custava 52px) e
// a coluna Produto RECOLHE (214px) — as duas juntas, com o aperto do # e das setas, somam os
// ~280px que fazem caber o 6o fornecedor num monitor de 1920.
const L_ORD = 26;
const L_FOTO = 52;
const L_SETAS = 26;
const L_DESC = 214;
const L_QTD = 46;
const C_DESC = 178;
// 66 -> 84 (DA-368): a linha I/D/F ganhou o 4o checkbox "C" (comprei) — 4x12px + 3 gaps de 6px
// = 66px de conteudo, que nao cabia mais nos 66-16=50px uteis da celula (padding de 8px cada lado).
const C_PRECO = 84;

// Deslocamento de cada coluna congelada (sticky) — derivado, nunca somado a mao no JSX:
// inserir uma coluna no meio ja errou o alinhamento por esquecer de somar em UM lugar.
// O offset da Qtd depende do recolhimento da Produto e por isso e calculado no componente.
const OFF_FOTO = L_ORD;
const OFF_SETAS = OFF_FOTO + L_FOTO;
const OFF_DESC = OFF_SETAS + L_SETAS;

// A foto encolheu de 40 pra 34 quando o codigo interno passou a morar embaixo dela. Numero
// LOAD-BEARING, medido na tela: a altura da linha e mandada pela celula mais alta, e essa
// passou a ser a da foto (foto + codigo + padding) — a coluna Produto pede so 40px. Com a
// foto em 40 a linha ia de 46 pra 61px e a vista perdia ~2 produtos na vertical, que e
// justamente o ganho pelo qual ela existe. Em 34 a linha fecha ~50px. Mexeu, remedir.
const FOTO_PX = 34;

const LS_DESC = 'cotacoes:lal:desc';

// Linha marcada em LILAS (+preto). NAO usa o amarelo `#fde68a` padrao do sistema porque aqui
// ele ja significa "menor unitario" — a linha marcada se confundia com a celula vencedora.
// Tambem NAO usa o azul `#bfdbfe` do [DA-301] (mesmo conflito, resolvido la no Monitor de
// Custos): o tema Azul Royal e azul por inteiro (`--panel-bg` #cfdef6, zebra #bdd2ef) e o azul
// da selecao some dentro da zebra — medido injetando na tela. Violeta esta livre de significado
// nesta grade (amarelo=menor unitario, verde=compra forcada) e e daltonismo-safe.
const SEL_BG = '#c4b5fd';

interface Props {
  /** Id da cotacao na URL — chaveia o que persiste por sessao (linha marcada). */
  cotacaoId: string;
  itens: ItemCotacao[];
  fornecedores: FornecedorVinculado[];
  buscaProduto: string;
  setBuscaProduto: (v: string) => void;
  numero: string;
  observacoes: string | null;
  dataAbertura: string | null;
  getResps: (itemId: number, fornId: number) => RespostaForm[];
  calcPrecoUnitario: (r: RespostaForm) => number;
  isDesconsiderado: (itemId: number, fornId: number, respIdx: number) => boolean;
  isForcarCompra: (itemId: number, fornId: number, respIdx: number) => boolean;
  /** PDF default = incluido; `true` significa OCULTO (mesma convencao da vista Por produto). */
  isOcultoPdf: (itemId: number, fornId: number, respIdx: number) => boolean;
  toggleDesconsiderar: (itemId: number, fornId: number, respIdx: number) => void;
  toggleForcarCompra: (itemId: number, fornId: number, respIdx: number) => void;
  toggleOcultoPdf: (itemId: number, fornId: number, respIdx: number) => void;
  custoDaCotacao: () => number;
  totalFornecedor: (fornId: number) => number;
  /* Recarregar catalogo + Salvar vivem TAMBEM aqui de proposito: a coluna vazia ("0 de 10
     itens") e onde o problema aparece, e a ferramenta que o resolve tem de morar na tela do
     problema. Sem isso a vista abria pela preferencia salva e o conserto ficava inalcancavel.
     Nao quebra o so-leitura: nenhum dos dois edita celula — um puxa da fonte, o outro grava. */
  recarregarCatalogo: () => void;
  recarregandoCat: boolean;
  salvarTudo: () => void;
  salvando: boolean;
  temAlteracoes: boolean;
  podeEditarCatalogo: boolean;
  /* Corrigir preco daqui: a celula mostra o UNITARIO, mas o que se edita e o preco da EMBALAGEM —
     por isso o gesto abre a caixinha de dois campos em vez de virar um input. Ausente = sem
     permissao ou cotacao fechada. A caixinha grava no Catalogo, que segue sendo a fonte. */
  abrirEditorPreco?: (
    item: ItemCotacao,
    forn: FornecedorVinculado,
    respIdx: number,
    rect: { top: number; left: number; bottom: number; right: number; width: number },
  ) => void;
  /** Move o item uma casa na fila. `idx` = posicao na lista COMPLETA (nao na filtrada). */
  moverItem: (idx: number, delta: number) => Promise<void>;
  /** Sai da vista lado a lado (opcionalmente abrindo um produto especifico na vista Por produto). */
  onSair: (itemId?: number) => void;
  onVoltarCotacoes: () => void;
  /** Filtro "Esconder ja comprei nesta rodada" (DA-368) — mesmo estado da vista Por produto
   *  (marcar aqui reflete la e vice-versa; e uma preferencia so, nao duas). */
  escondeCompradas: boolean;
  setEscondeCompradas: (v: boolean) => void;
  cotacaoCriadoEm: string | null;
  /** Desfaz uma marcacao de "comprado" feita por engano (toggle por linha). */
  onDesmarcarComprado: (item: ItemCotacao) => void;
  /** Abre a caixinha que corrige a DATA da compra pelo selo "últ. compra" (DA-368). */
  onEditarData: (
    item: ItemCotacao,
    rect: { top: number; left: number; bottom: number; right: number; width: number },
  ) => void;
  /** Marca/desmarca a celula produto x fornecedor (checkbox "C") — pode NAO ser o vencedor de
   *  amarelo, a escolha e do comprador (refinamento do DA-368: nao e mais um botao em lote por
   *  fornecedor que so marcava quem estava vencendo). */
  onMarcarCompradoCelula: (item: ItemCotacao, forn: FornecedorVinculado, comprado: boolean) => void;
}

/** O que a celula do par produto x fornecedor tem pra mostrar. */
interface CelulaOferta {
  resp: RespostaForm;
  uci: number;
  ignorado: boolean;
  forcado: boolean;
  /** quantas ofertas o fornecedor tem alem desta pro mesmo produto */
  extras: number;
  /** indice de cada oferta no array de respostas, pra alimentar o painel das ofertas */
  indices: number[];
  /** indice DESTA oferta (a que a celula representa) — a chave dos toggles I/D/F */
  idx: number;
}

export function CotacaoLadoALado(props: Props) {
  const {
    cotacaoId,
    itens, fornecedores, buscaProduto, setBuscaProduto, numero, observacoes, dataAbertura,
    getResps, calcPrecoUnitario, isDesconsiderado, isForcarCompra, isOcultoPdf,
    toggleDesconsiderar, toggleForcarCompra, toggleOcultoPdf,
    custoDaCotacao, totalFornecedor, moverItem, onSair, onVoltarCotacoes,
    recarregarCatalogo, recarregandoCat, salvarTudo, salvando, temAlteracoes,
    podeEditarCatalogo, abrirEditorPreco,
    escondeCompradas, setEscondeCompradas, cotacaoCriadoEm, onDesmarcarComprado,
    onEditarData, onMarcarCompradoCelula,
  } = props;

  // Painel de escolha entre as ofertas de um par item x fornecedor.
  const [escolha, setEscolha] = useState<{ itemId: number; fornId: number; indices: number[] } | null>(null);

  // Linha marcada (clicar de novo desmarca). So realce visual — nao guarda decisao.
  //
  // PERSISTE na sessao, como a da vista Por produto: F5 devolvia a grade no topo e sem marca,
  // e quem estava no item 40 perdia o lugar. Chave PROPRIA (`_linha_lal`) — as duas vistas
  // marcam por gestos diferentes (aqui clicar de novo desmarca, la nao) e mexer numa nao pode
  // mover a outra sem ninguem ter pedido.
  const [selecionado, setSelecionado] = useState<number | null>(() => {
    if (!cotacaoId) return null;
    try {
      const n = Number(sessionStorage.getItem(`cotacao_${cotacaoId}_linha_lal`));
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!cotacaoId) return;
    try {
      if (selecionado == null) sessionStorage.removeItem(`cotacao_${cotacaoId}_linha_lal`);
      else sessionStorage.setItem(`cotacao_${cotacaoId}_linha_lal`, String(selecionado));
    } catch {
      /* ignore */
    }
  }, [selecionado, cotacaoId]);

  // Rola ate a linha restaurada UMA vez, quando os itens chegam. So devolver o lilas deixaria a
  // marca fora da vista, que e o mesmo "perdi o lugar". `inline: 'nearest'` preserva a rolagem
  // HORIZONTAL — nesta grade ela e a posicao nas colunas de fornecedor, e mexer nela seria trocar
  // um lugar perdido por outro.
  const jaRolouAteSelecionada = useRef(false);
  useEffect(() => {
    if (jaRolouAteSelecionada.current || selecionado == null || itens.length === 0) return;
    jaRolouAteSelecionada.current = true;
    requestAnimationFrame(() => {
      document
        .getElementById(`lal-item-${selecionado}`)
        ?.scrollIntoView({ block: 'center', inline: 'nearest' });
    });
  }, [itens, selecionado]);

  // Coluna Produto recolhivel: e a maior da faixa fixa (214px) e o que se ganha recolhendo ela
  // e uma coluna de fornecedor inteira. RECOLHER, nao remover — a descricao do fornecedor nao
  // substitui a nossa (diverge, e fica vazia justamente onde o fornecedor nao tem o item), e o
  // "nosso custo" e a regua contra a qual os unitarios se comparam. Recolhida, os dois vao pro
  // `title` do codigo. Preferencia persiste como a da propria vista (principio: filtro de tela
  // sobrevive ao F5); default = ABERTA, pra quem nunca clicou ver a tela de sempre.
  const [descRecolhida, setDescRecolhida] = useState<boolean>(
    () => localStorage.getItem(LS_DESC) === 'recolhida',
  );
  useEffect(() => {
    localStorage.setItem(LS_DESC, descRecolhida ? 'recolhida' : 'aberta');
  }, [descRecolhida]);

  const OFF_QTD = descRecolhida ? OFF_DESC : OFF_DESC + L_DESC;

  // Reordenar so com a lista INTEIRA na tela: com busca ativa (ou o filtro "ja comprei nesta
  // rodada") a lista e um recorte, e mover uma casa dentro do recorte pularia por cima de itens
  // escondidos (mesma regra do DA-306).
  const podeReordenar = !buscaProduto.trim() && !escondeCompradas;

  // O painel e `fixed` e cobre exatamente a area de conteudo do app (`.app-content`), que fica
  // ABAIXO do menu e dos banners de alerta. Medir em vez de chutar altura: os banners aparecem
  // e somem sozinhos (conciliacao, contas a pagar...) e qualquer numero fixo erraria.
  const sentinelaRef = useRef<HTMLDivElement | null>(null);
  const [caixa, setCaixa] = useState({ top: 0, left: 0, width: 0, height: 0 });

  useLayoutEffect(() => {
    // Sobe do sentinela ate o primeiro ancestral que rola — e ele que define a area util.
    let alvo: HTMLElement | null = sentinelaRef.current?.parentElement ?? null;
    while (alvo && alvo !== document.body) {
      const ov = getComputedStyle(alvo).overflowY;
      if (ov === 'auto' || ov === 'scroll') break;
      alvo = alvo.parentElement;
    }
    const cont = alvo && alvo !== document.body ? alvo : null;

    function medir() {
      const r = (cont ?? document.documentElement).getBoundingClientRect();
      setCaixa({
        top: Math.max(0, Math.round(r.top)),
        left: Math.max(0, Math.round(r.left)),
        width: Math.round(r.width),
        height: Math.round(cont ? r.height : window.innerHeight - r.top),
      });
    }

    // Rolagem travada enquanto a vista esta aberta: o painel tem o scroll dele.
    const anterior = cont?.style.overflow ?? '';
    if (cont) {
      cont.scrollTop = 0;
      cont.style.overflow = 'hidden';
    }
    medir();
    window.addEventListener('resize', medir);
    return () => {
      window.removeEventListener('resize', medir);
      if (cont) cont.style.overflow = anterior;
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Esc fecha o painel de escolha primeiro; so sai da vista quando nao ha painel aberto.
      if (e.key !== 'Escape') return;
      if (escolha) setEscolha(null);
      else onSair();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onSair, escolha]);

  const itensVisiveis = useMemo(() => {
    // `itens` (a prop) fica intacto: os offsets sticky e o `moverItem(idx,...)` do componente
    // pai operam sobre a lista COMPLETA; so o que se RENDERIZA aqui e que filtra.
    let base = itens;
    if (escondeCompradas && cotacaoCriadoEm) {
      const corte = paraDataMs(cotacaoCriadoEm);
      base = base.filter((it) => !(it.ultima_compra_em && paraDataMs(it.ultima_compra_em) >= corte));
    }
    const q = buscaProduto.trim().toLowerCase();
    if (!q) return base;
    return base.filter((it) =>
      [it.codigo_interno, it.np_codigo, it.produto_descricao, it.descricao].some(
        (c) => c && String(c).toLowerCase().includes(q),
      ),
    );
  }, [itens, buscaProduto, escondeCompradas, cotacaoCriadoEm]);

  // Quantos o "Esconder ja comprei" tirou — sem o aviso, a lista vazia parecia cotacao sem produto.
  const ocultosComprados = useMemo(() => {
    if (!escondeCompradas || !cotacaoCriadoEm) return 0;
    const corte = paraDataMs(cotacaoCriadoEm);
    return itens.filter((it) => it.ultima_compra_em && paraDataMs(it.ultima_compra_em) >= corte).length;
  }, [itens, escondeCompradas, cotacaoCriadoEm]);

  /** O que mostrar na celula. Com 2+ ofertas, mostra a que CONCORRE: a forcada, senao a de menor
   *  unitario nao desconsiderada — a mesma que a vista Por produto pintaria de amarelo ([DA-834],
   *  que revogou o "pendente nao concorre" da DA-349). As outras ficam no +N. */
  function ofertaDaCelula(itemId: number, fornId: number): CelulaOferta | null {
    const linhas = getResps(itemId, fornId);
    const cands = linhas.map((r, ri) => ({
      resp: r,
      uci: calcPrecoUnitario(r),
      ignorado: isDesconsiderado(itemId, fornId, ri),
      forcado: isForcarCompra(itemId, fornId, ri),
      idx: ri,
    }));
    const comDado = cands.filter((c) => c.uci > 0 || c.resp.cod_produto_fornecedor || c.resp.descricao_fornecedor);
    if (comDado.length === 0) return null;
    const indices = comDado.map((c) => c.idx);
    const base = { extras: comDado.length - 1, indices };

    if (comDado.length === 1) return { ...comDado[0]!, ...base };

    const forcada = comDado.find((c) => c.forcado && c.uci > 0);
    if (forcada) return { ...forcada, ...base };
    const disputa = comDado.filter((c) => c.uci > 0 && !c.ignorado);
    if (disputa.length === 0) return { ...comDado[0]!, ...base };
    const menor = Math.min(...disputa.map((c) => c.uci));
    return { ...disputa.find((c) => c.uci === menor)!, ...base };
  }

  /** Menor unitario do item e se ha compra forcada — a MESMA conta da vista Por produto: toda
   *  oferta de todo fornecedor concorre, inclusive as 2+ do mesmo fornecedor ([DA-834]). */
  function rankingDoItem(itemId: number) {
    const candidatos = fornecedores.flatMap((f) =>
      getResps(itemId, f.fornecedor_id).map((r, ri) => ({
        uci: calcPrecoUnitario(r),
        ignorado: isDesconsiderado(itemId, f.fornecedor_id, ri),
        forcado: isForcarCompra(itemId, f.fornecedor_id, ri),
      })),
    );
    const temForcado = candidatos.some((c) => c.forcado && c.uci > 0);
    const validos = candidatos.filter((c) => c.uci > 0 && !c.ignorado);
    const menorPreco = validos.length > 0 ? Math.min(...validos.map((c) => c.uci)) : 0;
    return { menorPreco, temForcado };
  }

  // Quantos itens cada fornecedor cobre (tem entrada no catalogo) — mostra o buraco de cara.
  const cobertura = useMemo(() => {
    const c: Record<number, number> = {};
    for (const f of fornecedores) {
      let n = 0;
      for (const it of itens) if (ofertaDaCelula(it.id, f.fornecedor_id)) n++;
      c[f.fornecedor_id] = n;
    }
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fornecedores, itens, getResps]);

  const thBase: React.CSSProperties = {
    background: TH_BG,
    color: TH_FG,
    fontWeight: 700,
    textTransform: 'uppercase',
    position: 'sticky',
    zIndex: 3,
  };
  const celBase: React.CSSProperties = {
    borderBottom: '1px solid var(--border)',
    verticalAlign: 'top',
    padding: '5px 8px',
  };
  const btnSeta: React.CSSProperties = {
    background: 'none',
    border: 'none',
    padding: 0,
    lineHeight: 0,
    display: 'block',
  };
  // Celula sem catalogo: cor chapada, sem tramas. Vai como linear-gradient de uma cor so
  // pra COMPOR por cima do fundo da linha (zebra) em vez de troca-lo — assim o tom escurece
  // a celula vazia mantendo a zebra por baixo, e funciona nos temas claros e escuros.
  const vazioTint = 'linear-gradient(rgba(100,116,139,0.16), rgba(100,116,139,0.16))';

  const larguraFixa = OFF_QTD + L_QTD;

  /* Botao de recolher/abrir a coluna Produto. SOLIDO (fundo navy + seta branca) e nao um
     chevron solto: na 1a versao era so o icone na cor do cabecalho e o Ruan perguntou "nao
     era pra ter um botao?" — estava na tela e ele nao viu. Acao dentro de grade e botao,
     nunca um sinalzinho (mesma licao das acoes da aba Sessoes e do "+ Criar" do XML).
     Muda de casa de proposito: ABERTA ele vive no cabecalho da PROPRIA coluna Produto (e ali
     que o olho procura o controle dela); RECOLHIDA sobra so a coluna estreita, e ai ele e o
     unico vestigio — o olho vai atras porque a coluna sumiu. */
  const btnRecolher = (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setDescRecolhida((v) => !v); }}
      style={{
        background: TH_FG,
        border: 'none',
        borderRadius: 4,
        padding: '3px 4px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        lineHeight: 0,
      }}
      title={descRecolhida
        ? 'Mostrar a coluna Produto (nome + nosso custo)'
        : 'Recolher a coluna Produto — cabe mais um fornecedor na tela'}
    >
      {descRecolhida ? <ChevronsRight size={15} color="#fff" /> : <ChevronsLeft size={15} color="#fff" />}
    </button>
  );

  /** Identidade do item pro `title` do codigo — o que a coluna Produto mostraria se estivesse
   *  aberta (nome + nosso custo). So vale a pena montar com ela recolhida. */
  function tituloItem(it: ItemCotacao) {
    const nome = it.produto_descricao || it.descricao || '';
    const custo = it.preco_custo != null
      ? `\nnosso custo ${formatMoedaSemSimbolo(Number(it.preco_custo) || 0, 2)}`
      : '';
    const ultimaCompra = it.ultima_compra_em
      ? `\núlt. compra ${formatDataSP(it.ultima_compra_em)}${it.ultima_compra_fornecedor_nome ? ` · ${it.ultima_compra_fornecedor_nome}` : ''}`
      : '';
    return `${nome}${custo}${ultimaCompra}\n\nClique para abrir na vista Por produto`;
  }

  return (
    <>
      {/* sentinela: marca onde o conteudo da pagina comeca (o painel fixed ancora aqui) */}
      <div ref={sentinelaRef} style={{ height: 0 }} />

      <div
        style={{
          position: 'fixed',
          top: caixa.top,
          left: caixa.left,
          width: caixa.width || '100%',
          height: caixa.height || '100%',
          zIndex: 25,
          background: 'var(--page-bg)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Faixa fina — so o que sobrevive no modo tela inteira */}
        <div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '7px 14px',
            background: 'var(--panel-bg)',
            borderBottom: '1px solid var(--border)',
            boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
          }}
        >
          <button type="button" onClick={onVoltarCotacoes} className="btn-icon-header" title="Voltar para Cotacoes" style={{ padding: 6 }}>
            <ArrowLeft size={18} />
          </button>
          <h1 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-color)', whiteSpace: 'nowrap' }}>Cotacoes</h1>
          {/* Data EMBAIXO do numero (pedido dele, 21/10/2026): ao lado ela gastava ~95px da faixa. */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)', whiteSpace: 'nowrap' }}>Nº {numero}</span>
            {dataAbertura && (
              <span style={{ fontSize: 11, lineHeight: 1.2, padding: '1px 8px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--grid-row-alt-bg)', color: 'var(--text-color)', whiteSpace: 'nowrap' }}>
                {dataAbertura.split('-').reverse().join('/')}
              </span>
            )}
          </div>
          {observacoes && (
            <span
              title={observacoes}
              style={{ fontSize: 12, padding: '4px 10px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--grid-row-alt-bg)', color: 'var(--text-color)', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {observacoes}
            </span>
          )}

          <div style={{ position: 'relative', flex: '0 1 280px' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="text"
              placeholder="Filtrar produto..."
              value={buscaProduto}
              onChange={(e) => setBuscaProduto(upperMantendoCursor(e))}
              style={{ width: '100%', padding: '5px 10px 5px 30px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, background: 'var(--input-bg)', color: 'var(--text-color)' }}
            />
          </div>

          <span style={{ width: 1, height: 24, background: 'var(--border)' }} />

          <div style={{ display: 'flex', gap: 14, alignItems: 'center', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }} title="Oculta produtos cuja ultima compra foi feita depois que esta cotacao foi criada">
              <input type="checkbox" checked={escondeCompradas} onChange={(e) => setEscondeCompradas(e.target.checked)} />
              Esconder já comprei
            </label>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <button
              type="button"
              onClick={recarregarCatalogo}
              disabled={recarregandoCat}
              title="Puxa preço e dados do Catálogo do fornecedor — é o que preenche coluna vazia"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 6,
                border: '1px solid var(--primary)', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                background: recarregandoCat ? 'var(--border)' : 'var(--panel-bg)',
                color: recarregandoCat ? 'var(--text-muted)' : 'var(--primary)',
                cursor: recarregandoCat ? 'default' : 'pointer',
              }}
            >
              <RefreshCw size={13} className={recarregandoCat ? 'animate-spin' : undefined} />
              {recarregandoCat ? 'Buscando...' : 'Recarregar'}
            </button>
            {/* O aviso mora EMBAIXO do Salvar, nao ao lado: ao lado ele somava ~130px a faixa e, no zoom
                125% (janela util ~1540), empurrava o "Lado a lado" pra fora da tela (21/10/2026). */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <button
              type="button"
              onClick={salvarTudo}
              disabled={salvando || !temAlteracoes}
              title={temAlteracoes ? 'Grava as alterações no banco' : 'Nada a salvar'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: temAlteracoes && !salvando ? '3px 14px' : '6px 14px', borderRadius: 6, border: 'none',
                fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                background: salvando || !temAlteracoes ? 'var(--border)' : '#2563eb',
                color: salvando || !temAlteracoes ? 'var(--text-muted)' : '#fff',
                cursor: salvando || !temAlteracoes ? 'default' : 'pointer',
              }}
            >
              {salvando ? <Loader2 size={13} className="animate-spin" /> : null}
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
            {temAlteracoes && !salvando && (
              <span style={{ fontSize: 10, lineHeight: 1, fontWeight: 700, color: '#b45309', whiteSpace: 'nowrap' }}>
                Alterações não salvas
              </span>
            )}
            </div>
          </div>

          <div style={{ display: 'flex', border: '1px solid var(--primary)', borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => onSair()}
              style={{ border: 'none', padding: '6px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', background: 'var(--panel-bg)', color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}
              title="Voltar para a vista por produto (onde da pra editar)"
            >
              <List size={13} /> Por produto
            </button>
            <button
              type="button"
              style={{ border: 'none', padding: '6px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'default', background: 'var(--primary)', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}
            >
              <LayoutGrid size={13} /> Lado a lado
            </button>
          </div>
        </div>

        {/* Grade */}
        <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto', background: 'var(--panel-bg)' }}>
          <table
            style={{
              borderCollapse: 'separate',
              borderSpacing: 0,
              tableLayout: 'fixed',
              fontSize: 13,
              width: 'max-content',
              background: 'var(--panel-bg)',
            }}
          >
            <colgroup>
              <col style={{ width: L_ORD }} />
              <col style={{ width: L_FOTO }} />
              <col style={{ width: L_SETAS }} />
              {!descRecolhida && <col style={{ width: L_DESC }} />}
              <col style={{ width: L_QTD }} />
              {fornecedores.map((f) => (
                <Fragment key={f.fornecedor_id}>
                  <col style={{ width: C_DESC }} />
                  <col style={{ width: C_PRECO }} />
                </Fragment>
              ))}
            </colgroup>

            <thead>
              <tr>
                <th rowSpan={2} style={{ ...thBase, top: 0, left: 0, zIndex: 6, width: L_ORD, height: 64, fontSize: 11, padding: '0 4px 6px', verticalAlign: 'bottom', textAlign: 'center', borderBottom: `2px solid ${DIVISOR}` }}>#</th>
                <th rowSpan={2} style={{ ...thBase, top: 0, left: OFF_FOTO, zIndex: 6, width: L_FOTO, fontSize: 11, padding: '0 4px 6px', verticalAlign: 'bottom', textAlign: 'center', borderBottom: `2px solid ${DIVISOR}` }}>Cód.</th>
                {/* Coluna das setas: th vazio com a Produto aberta (o botao mora la dentro) e
                    o unico porta-botao quando ela recolhe. */}
                <th rowSpan={2} style={{ ...thBase, top: 0, left: OFF_SETAS, zIndex: 6, width: L_SETAS, padding: '0 0 6px', verticalAlign: 'bottom', textAlign: 'center', borderBottom: `2px solid ${DIVISOR}` }}>
                  {descRecolhida && btnRecolher}
                </th>
                {!descRecolhida && (
                  <th rowSpan={2} style={{ ...thBase, top: 0, left: OFF_DESC, zIndex: 6, width: L_DESC, fontSize: 11, padding: '0 8px 6px', verticalAlign: 'bottom', textAlign: 'left', borderBottom: `2px solid ${DIVISOR}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                      <span>Produto</span>
                      {btnRecolher}
                    </div>
                  </th>
                )}
                <th rowSpan={2} style={{ ...thBase, top: 0, left: OFF_QTD, zIndex: 6, width: L_QTD, fontSize: 11, padding: '0 6px 6px', verticalAlign: 'bottom', textAlign: 'center', borderBottom: `2px solid ${DIVISOR}`, borderRight: `3px solid ${DIVISOR}` }}>Qtd</th>
                {fornecedores.map((f) => (
                  <th
                    key={f.fornecedor_id}
                    colSpan={2}
                    style={{ ...thBase, top: 0, height: 38, padding: '4px 8px', textAlign: 'center', borderLeft: `2px solid ${DIVISOR}`, borderBottom: `1px solid ${DIVISOR}` }}
                  >
                    <div style={{ fontSize: 13, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {getNomeFornecedor(f)}
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.75, textTransform: 'none', letterSpacing: 0 }}>
                      {cobertura[f.fornecedor_id] || 0} de {itens.length} itens
                    </div>
                  </th>
                ))}
              </tr>
              <tr>
                {fornecedores.map((f) => (
                  <Fragment key={f.fornecedor_id}>
                    <th style={{ ...thBase, top: 38, height: 26, padding: '0 6px', fontSize: 11, letterSpacing: '0.3px', textAlign: 'left', borderBottom: `2px solid ${DIVISOR}` }}>
                      Desc fornec
                    </th>
                    <th style={{ ...thBase, top: 38, height: 26, padding: '0 6px', fontSize: 11, letterSpacing: '0.3px', textAlign: 'right', borderBottom: `2px solid ${DIVISOR}`, borderRight: `2px solid ${DIVISOR}` }}>
                      Unitário
                    </th>
                  </Fragment>
                ))}
              </tr>
            </thead>

            <tbody>
              {itensVisiveis.map((item, li) => {
                const { menorPreco, temForcado } = rankingDoItem(item.id);
                const zebra = li % 2 === 1;
                const sel = selecionado === item.id;
                const fundo = sel ? SEL_BG : zebra ? 'var(--grid-row-alt-bg)' : 'var(--panel-bg)';
                // Sobre o lilas o texto e SEMPRE escuro: `var(--text-color)` e claro no tema
                // escuro e sumiria na linha marcada. O muted e um violeta escuro da mesma
                // familia (ex-`#6b5310`, que era o marrom derivado do amarelo antigo).
                const cFg = sel ? '#000' : 'var(--text-color)';
                const cMuted = sel ? '#4c1d95' : 'var(--text-muted)';
                const qtdItem = parseFloat(item.quantidade) || 0;
                const idxReal = itens.indexOf(item);
                // Produto INATIVO: riscado, sem realce e sem I/D/F (pedido dele, 28/10/2026).
                const inativo = itemInativo(item);

                return (
                  <tr
                    key={item.id}
                    id={`lal-item-${item.id}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelecionado((p) => (p === item.id ? null : item.id))}
                    onDoubleClick={() => onSair(item.id)}
                    title="1 clique marca a linha · 2 cliques abre o produto na vista Por produto"
                  >
                    <td style={{ ...celBase, position: 'sticky', left: 0, zIndex: 2, background: fundo, padding: '5px 2px', textAlign: 'center', fontWeight: 700, color: cMuted }}>
                      {idxReal + 1}
                    </td>
                    {/* Foto + codigo interno EMPILHADOS numa coluna so — a coluna Cod. propria
                        custava 52px e cabe debaixo da foto sem esticar a linha (a altura ja e
                        mandada pela coluna Produto quando ela esta aberta).
                        O codigo segue sendo o atalho de 1 clique pra abrir o produto — o
                        duplo-clique da linha trava em algumas telas, entao ele nunca fica
                        sozinho (mesmo par da CotacoesHome, onde o NUMERO abre e a linha marca).
                        Com a Produto recolhida o `title` carrega o nome e o nosso custo: e o
                        unico lugar onde a identidade do item ainda se le. */}
                    <td style={{ ...celBase, position: 'sticky', left: OFF_FOTO, zIndex: 2, background: fundo, padding: '2px 3px', textAlign: 'center' }}>
                      {item.foto_url ? (
                        <img src={item.foto_url} alt="" style={{ width: FOTO_PX, height: FOTO_PX, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
                      ) : null}
                      {item.codigo_interno ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onSair(item.id); }}
                          style={{ background: 'transparent', border: 'none', padding: 0, fontWeight: 700, fontSize: 11, lineHeight: 1, color: 'var(--primary)', textDecoration: 'underline', cursor: 'pointer', display: 'block', width: '100%' }}
                          title={descRecolhida ? tituloItem(item) : 'Abrir este produto na vista Por produto'}
                        >
                          {item.codigo_interno}
                        </button>
                      ) : item.np_codigo ? (
                        // Item da prateleira nova ([DA-610]): NAO e' botao — ele nao existe na
                        // vista Por produto como produto, entao levar pra la nao abriria nada.
                        // O selo lilas e' o mesmo do resto do sistema.
                        <span style={{ fontSize: 10, lineHeight: 1.3, background: '#c4b5fd', color: '#000', borderRadius: 3, padding: '1px 4px', fontWeight: 700, display: 'inline-block' }}
                              title="Item da prateleira Novos Produtos — ainda nao virou produto do cadastro">
                          {item.np_codigo}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, lineHeight: 1, color: cMuted, display: 'block' }}>—</span>
                      )}
                    </td>
                    {/* Setas da fila do item — molde da CotacoesHome: coluna propria estreita,
                        as duas empilhadas na vertical, azul do tema, apagadas nas bordas. */}
                    <td style={{ ...celBase, position: 'sticky', left: OFF_SETAS, zIndex: 2, background: fundo, padding: 4, textAlign: 'center' }}>
                      {podeReordenar && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); void moverItem(idxReal, -1); }}
                            disabled={idxReal === 0}
                            style={{ ...btnSeta, opacity: idxReal === 0 ? 0.25 : 1, cursor: idxReal === 0 ? 'default' : 'pointer' }}
                            title="Mover para cima"
                          >
                            <ChevronUp size={16} color="var(--primary)" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); void moverItem(idxReal, 1); }}
                            disabled={idxReal === itens.length - 1}
                            style={{ ...btnSeta, opacity: idxReal === itens.length - 1 ? 0.25 : 1, cursor: idxReal === itens.length - 1 ? 'default' : 'pointer' }}
                            title="Mover para baixo"
                          >
                            <ChevronDown size={16} color="var(--primary)" />
                          </button>
                        </div>
                      )}
                    </td>
                    {!descRecolhida && (
                      <td style={{ ...celBase, position: 'sticky', left: OFF_DESC, zIndex: 2, background: fundo }}>
                        <div
                          style={{ fontWeight: 600, lineHeight: 1.2, fontSize: 12.5, color: cFg, ...(inativo ? { textDecoration: 'line-through' } : {}) }}
                          title={inativo ? `${MSG_PRODUTO_INATIVO}. Fora do total e do que vai pro fornecedor.` : undefined}
                        >
                          {inativo && <SeloInativo />}
                          {item.produto_descricao || item.descricao || '—'}
                        </div>
                        {item.preco_custo != null && (
                          <div style={{ fontSize: 10.5, color: cMuted, marginTop: 2 }}>
                            nosso custo <b style={{ color: cFg }}>{formatMoedaSemSimbolo(Number(item.preco_custo) || 0, 2)}</b>
                          </div>
                        )}
                        {item.ultima_compra_em ? (
                          <div style={{ marginTop: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onEditarData(item, e.currentTarget.getBoundingClientRect()); }}
                              style={{ background: 'var(--primary)', border: 'none', padding: '2px 6px', margin: 0, cursor: 'pointer', color: '#fff', fontSize: 10, textAlign: 'left', fontFamily: 'inherit', fontWeight: 700, borderRadius: 10 }}
                              title="Ajustar a data da compra"
                            >
                              🛒 últ. compra {formatDataSP(item.ultima_compra_em)}
                              {item.ultima_compra_fornecedor_nome ? ` · ${item.ultima_compra_fornecedor_nome}` : ''}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onDesmarcarComprado(item); }}
                              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex' }}
                              title="Desmarcar (desfazer 'comprado')"
                            >
                              <X size={10} color={cMuted} />
                            </button>
                          </div>
                        ) : item.ultima_entrada_xml_em ? (
                          <div style={{ marginTop: 1 }}>
                            <span
                              style={{ background: 'var(--primary)', padding: '2px 6px', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 10, opacity: 0.6 }}
                              title="Data da última entrada via XML (sem marcação de compra nesta cotação)"
                            >
                              📦 últ. entrada {formatDataSP(item.ultima_entrada_xml_em)}
                              {item.ultima_entrada_xml_fornecedor_nome ? ` · ${item.ultima_entrada_xml_fornecedor_nome}` : ''}
                            </span>
                          </div>
                        ) : null}
                      </td>
                    )}
                    <td style={{ ...celBase, position: 'sticky', left: OFF_QTD, zIndex: 2, background: fundo, textAlign: 'center', fontWeight: 700, color: cFg, borderRight: `3px solid ${DIVISOR}` }}>
                      {formatQtd(qtdItem)}
                    </td>

                    {fornecedores.map((f) => {
                      const of = ofertaDaCelula(item.id, f.fornecedor_id);
                      if (!of) {
                        const vazia: React.CSSProperties = {
                          ...celBase,
                          background: fundo,
                          backgroundImage: vazioTint,
                          textAlign: 'center',
                          color: sel ? 'rgba(0,0,0,0.35)' : 'var(--border)',
                        };
                        return (
                          <Fragment key={f.fornecedor_id}>
                            <td style={{ ...vazia, borderLeft: `2px solid ${DIVISOR}` }}>—</td>
                            <td style={{ ...vazia, borderRight: `2px solid ${DIVISOR}` }}>—</td>
                          </Fragment>
                        );
                      }
                      const isMenor = !inativo &&
                        ((of.uci > 0 && menorPreco > 0 && of.uci === menorPreco && !of.ignorado && !temForcado) || of.forcado);
                      const realce = of.ignorado
                        ? { textDecoration: 'line-through' as const, color: cMuted }
                        : of.forcado
                          ? { background: '#d1fae5', color: '#065f46' }
                          : isMenor
                            ? { background: '#fde68a', color: '#000' }
                            : {};
                      const totalLinha = of.uci * qtdItem;

                      return (
                        <Fragment key={f.fornecedor_id}>
                          <td style={{ ...celBase, background: fundo, borderLeft: `2px solid ${DIVISOR}` }}>
                            <div style={{ fontSize: 10.5, fontWeight: 700, color: cMuted, display: 'flex', gap: 6, alignItems: 'center' }}>
                              <span>{of.resp.cod_produto_fornecedor || 'sem código'}</span>
                              {of.resp.embalagem && (
                                <span style={{ background: sel ? 'rgba(0,0,0,0.12)' : 'var(--border)', color: cFg, borderRadius: 3, padding: '0 4px', fontSize: 9.5 }}>
                                  {of.resp.embalagem}
                                </span>
                              )}
                            </div>
                            <div
                              title={of.resp.descricao_fornecedor || ''}
                              style={{ fontSize: 11.5, lineHeight: 1.25, color: cFg, marginTop: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                            >
                              {of.resp.descricao_fornecedor || '—'}
                            </div>
                          </td>
                          <td
                            style={{ ...celBase, background: fundo, textAlign: 'right', borderRight: `2px solid ${DIVISOR}`, ...realce }}
                            title={[
                              of.ignorado ? 'Desconsiderado nesta cotacao'
                                : of.forcado ? 'Compra forcada'
                                  : isMenor ? 'Menor preco deste item (unitario)'
                                    : 'Preco Emb ÷ Qtd na Emb',
                              // O subtotal cedeu o lugar aos checkboxes, mas nao se perdeu:
                              // quem precisar do "unitario x qtd" acha aqui.
                              of.uci > 0 ? `Unitário × qtd = ${formatMoedaSemSimbolo(totalLinha, 2)}` : '',
                              abrirEditorPreco && !inativo ? 'Clique no valor para corrigir o preço no Catálogo' : 'Preço vem do Catálogo do fornecedor',
                            ].filter(Boolean).join(' · ')}
                          >
                            <div style={{ fontWeight: 700, fontSize: isMenor ? 14 : 13, whiteSpace: 'nowrap' }}>
                              {/* O numero abre a caixinha de preco. `stopPropagation` porque a linha
                                  inteira marca no clique e sai da vista no duplo-clique. */}
                              <span
                                onClick={(e) => {
                                  if (!abrirEditorPreco || inativo) return;
                                  e.stopPropagation();
                                  const alvo = (e.currentTarget.closest('td') ?? e.currentTarget) as HTMLElement;
                                  abrirEditorPreco(item, f, of.idx, alvo.getBoundingClientRect());
                                }}
                                onDoubleClick={(e) => { if (abrirEditorPreco && !inativo) e.stopPropagation(); }}
                                style={abrirEditorPreco && !inativo
                                  ? { cursor: 'pointer', borderBottom: '1px dashed currentColor' }
                                  : undefined}
                              >
                                {of.uci > 0 ? formatMoedaSemSimbolo(of.uci, 4) : '—'}
                              </span>
                              {of.extras > 0 && !inativo && (
                                <button
                                  type="button"
                                  onClick={() => setEscolha({ itemId: item.id, fornId: f.fornecedor_id, indices: of.indices })}
                                  title={`${of.extras} outra(s) oferta(s) deste fornecedor pro mesmo produto — clique pra ver`}
                                  style={{ marginLeft: 5, background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, padding: '0 5px', fontSize: 9.5, fontWeight: 700, cursor: 'pointer', verticalAlign: 2 }}
                                >
                                  +{of.extras}
                                </button>
                              )}
                            </div>
                            {/* I / D / F — os mesmos controles da vista Por produto, na celula
                                que o comprador esta olhando quando decide. `stopPropagation`
                                porque a linha inteira marca no clique e sai no duplo-clique. */}
                            <div
                              style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center', marginTop: 3 }}
                              onClick={(e) => e.stopPropagation()}
                              onDoubleClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={!isOcultoPdf(item.id, f.fornecedor_id, of.idx)}
                                onChange={() => toggleOcultoPdf(item.id, f.fornecedor_id, of.idx)}
                                disabled={inativo}
                                title={inativo ? MSG_PRODUTO_INATIVO : 'Incluir no PDF e WhatsApp (I)'}
                                style={{ width: 12, height: 12, accentColor: 'var(--primary)', cursor: inativo ? 'not-allowed' : 'pointer', margin: 0 }}
                              />
                              <input
                                type="checkbox"
                                checked={of.ignorado}
                                onChange={() => toggleDesconsiderar(item.id, f.fornecedor_id, of.idx)}
                                disabled={inativo}
                                title={inativo ? MSG_PRODUTO_INATIVO : 'Desconsiderar do cálculo do menor preço (D)'}
                                style={{ width: 12, height: 12, accentColor: '#f97316', cursor: inativo ? 'not-allowed' : 'pointer', margin: 0 }}
                              />
                              {/* F so existe pra oferta com preco e nao desconsiderada — mesma
                                  regra da Por produto; o vao mantem os outros dois alinhados. */}
                              {of.uci > 0 && !of.ignorado && !inativo ? (
                                <input
                                  type="checkbox"
                                  checked={of.forcado}
                                  onChange={() => toggleForcarCompra(item.id, f.fornecedor_id, of.idx)}
                                  title="Forçar compra deste item (F)"
                                  style={{ width: 12, height: 12, accentColor: '#059669', cursor: 'pointer', margin: 0 }}
                                />
                              ) : (
                                <span style={{ width: 12, display: 'inline-block' }} />
                              )}
                              {/* C (comprei) — DA-368, refinado: marca o produto como comprado
                                  DESTE fornecedor, que pode NAO ser o vencedor de amarelo. So um
                                  fornecedor por produto fica marcado — marcar aqui move a marca de
                                  outra celula do mesmo produto sozinho (o backend sobrescreve). So
                                  aparece com oferta de verdade e item com onde guardar a marca
                                  (produto ou prateleira Novos Produtos). */}
                              {alvoDaMarcaCompra(item) != null && of.uci > 0 ? (() => {
                                const compradoAqui = item.compra_em_aberto === true && item.ultima_compra_fornecedor_id === f.fornecedor_id;
                                return (
                                  <button
                                    type="button"
                                    onClick={() => onMarcarCompradoCelula(item, f, !compradoAqui)}
                                    title={compradoAqui ? 'Comprado deste fornecedor — clique pra desmarcar' : 'Comprei deste fornecedor — marca o produto como comprado'}
                                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 16, padding: 0, border: 'none', borderRadius: 4, background: compradoAqui ? '#2563eb' : 'transparent', cursor: 'pointer' }}
                                  >
                                    <ShoppingCart size={13} color={compradoAqui ? '#fff' : '#2563eb'} />
                                  </button>
                                );
                              })() : (
                                <span style={{ width: 18, display: 'inline-block' }} />
                              )}
                            </div>
                          </td>
                        </Fragment>
                      );
                    })}
                  </tr>
                );
              })}
              {itens.length > 0 && itensVisiveis.length === 0 && (
                <tr>
                  {/* colSpan alto cobre qualquer numero de fornecedores; o sticky segura o texto na vista com a rolagem horizontal. */}
                  <td colSpan={1000} style={{ padding: 0 }}>
                    <div style={{ position: 'sticky', left: 0, display: 'inline-block', padding: '32px 24px', color: 'var(--text-muted)' }}>
                      {buscaProduto.trim() && <>Nenhum item da cotação corresponde a “{buscaProduto}”.</>}
                      {ocultosComprados > 0 && (
                        <AvisoOcultosComprados qtd={ocultosComprados} onMostrar={() => setEscondeCompradas(false)} />
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>

            <tfoot>
              <tr>
                <td colSpan={descRecolhida ? 3 : 4} style={{ position: 'sticky', bottom: 0, left: 0, zIndex: 5, background: TH_FG, color: '#e0f2fe', fontSize: 11, fontWeight: 700, padding: '7px 8px', borderTop: `2px solid ${DIVISOR}`, width: larguraFixa - L_QTD }}>
                  Total do fornecedor
                  {/* Nosso custo e' um numero da cotacao, nao de um fornecedor: mora aqui, uma vez so
                      (pedido do Ruan, 30/10/2026 — antes cada coluna tinha a "Soma" do preco dele). */}
                  <span style={{ marginLeft: 10, fontWeight: 600, opacity: 0.75 }}>
                    · nosso custo {formatMoeda(custoDaCotacao())}
                  </span>
                </td>
                <td style={{ position: 'sticky', bottom: 0, left: OFF_QTD, zIndex: 5, background: TH_FG, borderTop: `2px solid ${DIVISOR}`, borderRight: `3px solid ${DIVISOR}` }} />
                {fornecedores.map((f) => (
                  <Fragment key={f.fornecedor_id}>
                    <td style={{ position: 'sticky', bottom: 0, zIndex: 3, background: TH_FG, color: '#e0f2fe', fontSize: 9.5, fontWeight: 600, padding: '7px 8px', borderTop: `2px solid ${DIVISOR}`, borderLeft: `2px solid ${DIVISOR}`, opacity: 0.75, textTransform: 'uppercase' }}>
                    </td>
                    <td style={{ position: 'sticky', bottom: 0, zIndex: 3, background: TH_FG, color: '#e0f2fe', fontSize: 12.5, fontWeight: 700, padding: '7px 8px', textAlign: 'right', whiteSpace: 'nowrap', borderTop: `2px solid ${DIVISOR}`, borderRight: `2px solid ${DIVISOR}` }}>
                      {formatMoeda(totalFornecedor(f.fornecedor_id))}
                    </td>
                  </Fragment>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Legenda */}
        <div style={{ flex: '0 0 auto', padding: '6px 14px', fontSize: 11, color: 'var(--text-muted)', display: 'flex', gap: 18, alignItems: 'center', background: 'var(--panel-bg)', borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <span><Amostra cor="#fde68a" /> menor unitário do produto</span>
          <span><Amostra cor="#d1fae5" /> compra forçada</span>
          <span><Amostra cor="rgba(100,116,139,0.16)" /> fornecedor não tem o item no catálogo</span>
          <span>
            <span style={{ background: 'var(--primary)', color: '#fff', borderRadius: 8, padding: '0 5px', fontSize: 9.5, fontWeight: 700 }}>+1</span>
            {' '}o fornecedor tem mais de uma oferta pro mesmo produto (mostra a mais barata)
          </span>
          {/* Checkbox sem rotulo nao se explica sozinho — a legenda e que diz o que cada um faz
              (o `title` de cada um repete, mas so aparece parando o mouse em cima). */}
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked readOnly style={{ width: 12, height: 12, accentColor: 'var(--primary)', margin: 0 }} />
            incluir no PDF
            <input type="checkbox" checked readOnly style={{ width: 12, height: 12, accentColor: '#f97316', margin: 0, marginLeft: 8 }} />
            desconsiderar
            <input type="checkbox" checked readOnly style={{ width: 12, height: 12, accentColor: '#059669', margin: 0, marginLeft: 8 }} />
            forçar compra
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 16, borderRadius: 4, background: '#2563eb', marginLeft: 8 }}>
              <ShoppingCart size={13} color="#fff" />
            </span>
            comprei (marca o produto como comprado deste fornecedor)
          </span>
          <span>o <b>unitário × qtd pedida</b> saiu da célula — parando o mouse sobre o preço ele aparece</span>
        </div>

        {escolha && (
          <PainelEscolha
            item={itens.find((i) => i.id === escolha.itemId)}
            fornecedor={fornecedores.find((f) => f.fornecedor_id === escolha.fornId)}
            linhas={getResps(escolha.itemId, escolha.fornId)}
            indices={escolha.indices}
            calcPrecoUnitario={calcPrecoUnitario}
            isDesconsiderado={(idx) => isDesconsiderado(escolha.itemId, escolha.fornId, idx)}
            isForcarCompra={(idx) => isForcarCompra(escolha.itemId, escolha.fornId, idx)}
            toggleDesconsiderar={(idx) => toggleDesconsiderar(escolha.itemId, escolha.fornId, idx)}
            toggleForcarCompra={(idx) => toggleForcarCompra(escolha.itemId, escolha.fornId, idx)}
            onFechar={() => setEscolha(null)}
          />
        )}
      </div>
    </>
  );
}

/** Lista as ofertas do par. Todas concorrem ([DA-834]); o que o comprador decide aqui e o mesmo
 *  da vista Por produto: desconsiderar (D) ou forcar (F) cada oferta. */
function PainelEscolha(props: {
  item?: ItemCotacao;
  fornecedor?: FornecedorVinculado;
  linhas: RespostaForm[];
  indices: number[];
  calcPrecoUnitario: (r: RespostaForm) => number;
  isDesconsiderado: (idx: number) => boolean;
  isForcarCompra: (idx: number) => boolean;
  toggleDesconsiderar: (idx: number) => void;
  toggleForcarCompra: (idx: number) => void;
  onFechar: () => void;
}) {
  const { item, fornecedor, linhas, indices, calcPrecoUnitario, isDesconsiderado, isForcarCompra, toggleDesconsiderar, toggleForcarCompra, onFechar } = props;
  const menor = Math.min(
    ...indices.filter((i) => !isDesconsiderado(i)).map((i) => calcPrecoUnitario(linhas[i]!)).filter((v) => v > 0),
  );

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div style={{ background: 'var(--panel-bg)', borderRadius: 10, width: 640, maxWidth: '92vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div className="modal-header" style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-color)' }}>Ofertas deste fornecedor</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {fornecedor ? getNomeFornecedor(fornecedor) : ''} · {item?.produto_descricao || item?.descricao || ''}
          </div>
        </div>

        <div style={{ overflowY: 'auto', padding: 10 }}>
          {indices.map((i) => {
            const l = linhas[i]!;
            const uci = calcPrecoUnitario(l);
            const ignorado = isDesconsiderado(i);
            const forcado = isForcarCompra(i);
            const eMenor = uci > 0 && uci === menor && !ignorado;
            return (
              <div
                key={i}
                style={{
                  display: 'flex', width: '100%', textAlign: 'left', gap: 12, alignItems: 'center',
                  padding: '10px 12px', marginBottom: 8, borderRadius: 8,
                  border: '1px solid var(--border)', background: 'var(--panel-bg)',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* A UF vem PRIMEIRO (DA-391): quando as duas ofertas sao as duas filiais do
                      mesmo codigo, codigo/descricao/embalagem sao identicos e este selo e a UNICA
                      coisa que distingue os dois botoes. */}
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>
                    {l.filial_uf && <SeloFilial uf={l.filial_uf} daCasa={l.filial_principal} />}
                    {l.cod_produto_fornecedor || 'sem código'}
                    {l.embalagem ? ` · ${l.embalagem}` : ''}
                    {Number(l.quantidade_embalagem) > 1 ? ` · ${l.quantidade_embalagem} por emb.` : ''}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-color)', marginTop: 2 }}>
                    {l.descricao_fornecedor || '—'}
                  </div>
                </div>
                <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <div
                    style={{
                      fontWeight: 700, fontSize: 14,
                      ...(ignorado ? { textDecoration: 'line-through', color: 'var(--text-muted)' } : { color: 'var(--text-color)' }),
                      ...(forcado ? { background: '#d1fae5', color: '#065f46', borderRadius: 4, padding: '1px 6px' } : eMenor ? { background: '#fde68a', color: '#000', borderRadius: 4, padding: '1px 6px' } : {}),
                    }}
                  >
                    {uci > 0 ? formatMoedaSemSimbolo(uci, 4) : '—'}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                    {ignorado ? 'desconsiderada' : forcado ? 'compra forçada' : eMenor ? 'menor unitário' : 'unitário'}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--text-color)', whiteSpace: 'nowrap' }}>
                  <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={ignorado}
                      onChange={() => toggleDesconsiderar(i)}
                      style={{ width: 12, height: 12, accentColor: '#f97316', cursor: 'pointer', margin: 0 }}
                    />
                    desconsiderar
                  </label>
                  {/* F so pra oferta com preco e nao desconsiderada — mesma regra da celula. */}
                  {uci > 0 && !ignorado && (
                    <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={forcado}
                        onChange={() => toggleForcarCompra(i)}
                        style={{ width: 12, height: 12, accentColor: '#059669', cursor: 'pointer', margin: 0 }}
                      />
                      forçar compra
                    </label>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1 }}>
            Todas concorrem pelo menor unitário. Vale só nesta cotação — o catálogo não muda.
          </span>
          <button
            type="button"
            onClick={onFechar}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--panel-bg)', color: 'var(--text-color)', fontSize: 13, cursor: 'pointer' }}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

function Amostra({ cor }: { cor: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        borderRadius: 3,
        verticalAlign: -2,
        marginRight: 5,
        border: '1px solid var(--border)',
        background: cor,
      }}
    />
  );
}
